import type { ProductBarcodeType } from '../model/types';

/**
 * Client-side mirror of `web-catalog-master-service`'s `ProductBarcodeService` normalization +
 * `AttributeValidationSupport` GS1 mod-10 checksum rule (user-story-catalog-013, reused there for
 * user-story-catalog-020) — lets the "Add barcode" dialog reject an obviously-bad value before a
 * round trip, while the server remains the source of truth (it re-validates identically).
 */

/** Standard, checksum-bearing GTIN digit lengths this UI auto-detects a type for. */
const STANDARD_DIGIT_LENGTHS = new Set([8, 12, 13, 14]);

/** Strips spaces/dashes (mirrors the server's own separator-stripping for digit-shaped values). */
export function normalizeBarcodeValue(raw: string): string {
  return raw.trim().replace(/[\s-]/g, '');
}

/**
 * GS1 mod-10 check digit for the digits preceding it: from the rightmost digit leftward, weights
 * alternate 3, 1, 3, 1…; `checkDigit = (10 - (sum % 10)) % 10`. Valid for GTIN-8/12/13/14 alike —
 * the weighting is anchored to the check digit's position, not the overall length.
 */
export function gs1CheckDigit(digitsWithoutCheck: string): number {
  let sum = 0;
  for (let i = 0; i < digitsWithoutCheck.length; i += 1) {
    const digit = Number(digitsWithoutCheck[digitsWithoutCheck.length - 1 - i]);
    sum += digit * (i % 2 === 0 ? 3 : 1);
  }
  return (10 - (sum % 10)) % 10;
}

/** True when `value` is all digits and its last digit is the correct GS1 mod-10 check digit. */
export function isValidGs1Checksum(value: string): boolean {
  if (!/^\d+$/.test(value) || value.length < 2) return false;
  const checkDigit = Number(value[value.length - 1]);
  return gs1CheckDigit(value.slice(0, -1)) === checkDigit;
}

/**
 * Auto-detects a type by digit length only — mirrors the server default exactly (8→EAN8,
 * 12→UPC_A, 13→EAN13, 14→GTIN14, else OTHER). Never returns `CODE128` (never length-inferred,
 * either here or server-side); a CODE128 value must be submitted via the explicit "non-standard"
 * path (`analyzeBarcodeValue`'s caller sends `type: 'OTHER'`).
 */
export function detectBarcodeTypeByLength(digits: string): ProductBarcodeType {
  switch (digits.length) {
    case 8:
      return 'EAN8';
    case 12:
      return 'UPC_A';
    case 13:
      return 'EAN13';
    case 14:
      return 'GTIN14';
    default:
      return 'OTHER';
  }
}

export interface BarcodeValueAnalysis {
  /** Separator-stripped value — what actually gets submitted. */
  normalized: string;
  /** Whether `normalized` is non-empty and every character is a digit. */
  isDigitsOnly: boolean;
  /** Best-guess type by length; only meaningful (and only shown) when `isStandardLength`. */
  detectedType: ProductBarcodeType;
  /** True for the four standard, checksum-bearing lengths (8/12/13/14). */
  isStandardLength: boolean;
  /** `null` when a checksum doesn't apply (not all-digits, or a non-standard length). */
  checksumValid: boolean | null;
}

/** Analyzes a raw "Add barcode" input for the standard (non-"non-standard-code") submit path. */
export function analyzeBarcodeValue(raw: string): BarcodeValueAnalysis {
  const normalized = normalizeBarcodeValue(raw);
  const isDigitsOnly = normalized.length > 0 && /^\d+$/.test(normalized);
  const isStandardLength = isDigitsOnly && STANDARD_DIGIT_LENGTHS.has(normalized.length);
  const detectedType = isStandardLength ? detectBarcodeTypeByLength(normalized) : 'OTHER';
  return {
    normalized,
    isDigitsOnly,
    detectedType,
    isStandardLength,
    checksumValid: isStandardLength ? isValidGs1Checksum(normalized) : null,
  };
}
